import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api, { getApiErrorMessage } from '../../services/api';

export default function QrScanner() {
    const [scanResult, setScanResult] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [mode, setMode] = useState('qr');
    const [eventId, setEventId] = useState(() => sessionStorage.getItem('staffEventId') || '');
    const [events, setEvents] = useState([]);
    const [dniInput, setDniInput] = useState('');
    
    const html5QrCode = useRef(null);
    const isScanningRef = useRef(false);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await api.get('/events');
                setEvents(response.data);
            } catch (err) {
                console.error("Error fetching events", err);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        return () => {
            if (html5QrCode.current && isScanningRef.current) {
                html5QrCode.current.stop().catch(e => console.error("Failed to stop on unmount", e));
            }
        };
    }, []);

    const handleEventChange = (e) => {
        const value = e.target.value;
        setEventId(value);
        if (value) {
            sessionStorage.setItem('staffEventId', value);
        } else {
            sessionStorage.removeItem('staffEventId');
        }
    };

    const startScanner = async () => {
        if (!eventId) {
            setError('Selecciona un evento');
            return;
        }
        setIsCameraOpen(true);
        setError('');
        setScanResult(null);
        
        setTimeout(async () => {
            try {
                if (!html5QrCode.current) {
                    html5QrCode.current = new Html5Qrcode("reader");
                }
                
                await html5QrCode.current.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 }
                    },
                    async (decodedText) => {
                        if (!isScanningRef.current) return;
                        isScanningRef.current = false;
                        
                        try {
                            await html5QrCode.current.stop();
                        } catch(e) {
                            console.error("Error stopping scanner", e);
                        }
                        
                        setIsCameraOpen(false);
                        setLoading(true);
                        
                        try {
                            const response = await api.post('/staff/validate-entry', { 
                                event_id: parseInt(eventId), 
                                method: 'qr',
                                qr_code_uuid: decodedText 
                            });
                            setScanResult(response.data);
                            setError('');
                        } catch (e) {
                            const data = e.response?.data;
                            setError(getApiErrorMessage(e, 'Codigo QR invalido o no reconocido'));
                            setScanResult(e.response?.status < 500 ? (data || null) : null);
                        } finally {
                            setLoading(false);
                        }
                    },
                    () => {}
                );
                isScanningRef.current = true;
            } catch (err) {
                console.error("Error starting scanner:", err);
                setError("Error al acceder a la camara. Asegurese de dar permisos.");
                setIsCameraOpen(false);
                isScanningRef.current = false;
            }
        }, 100);
    };

    const stopScanner = async () => {
        if (html5QrCode.current && isScanningRef.current) {
            try {
                await html5QrCode.current.stop();
            } catch (err) {
                console.error("Failed to stop scanner", err);
            }
        }
        isScanningRef.current = false;
        setIsCameraOpen(false);
    };

    const validateByDni = async (e) => {
        e.preventDefault();
        if (!eventId) {
            setError('Selecciona un evento');
            return;
        }
        if (!dniInput) {
            setError('Ingresa el DNI');
            return;
        }
        setLoading(true);
        setError('');
        setScanResult(null);
        try {
            const response = await api.post('/staff/validate-entry', { 
                event_id: parseInt(eventId), 
                method: 'dni',
                document_number: dniInput 
            });
            setScanResult(response.data);
        } catch (e) {
            const data = e.response?.data;
            setError(getApiErrorMessage(e, 'DNI no encontrado'));
            setScanResult(e.response?.status < 500 ? (data || null) : null);
        } finally {
            setLoading(false);
        }
    };

    const handleRescan = () => {
        setScanResult(null);
        setError('');
        if (mode === 'qr') {
            startScanner();
        }
    };

    return (
        <div className="max-w-md mx-auto bg-gray-100 rounded-lg overflow-hidden shadow-2xl min-h-[80vh] flex flex-col items-center">
            <h2 className="text-2xl font-bold py-4 text-emerald-700 w-full text-center bg-white shadow-sm border-b border-gray-200">Validacion de entradas</h2>

            <div className="flex-grow w-full relative p-4 flex flex-col items-center justify-center">
                <div className="w-full mb-4 bg-white p-3 rounded-lg shadow-sm space-y-2">
                    <div>
                        <label className="text-xs font-semibold text-gray-600">Evento</label>
                        <select className="w-full p-2 border rounded bg-white" value={eventId} onChange={handleEventChange}>
                            <option value="">Selecciona un evento</option>
                            {events.map(event => (
                                <option key={event.id} value={event.id}>{event.title}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setMode('qr')} className={`flex-1 p-2 rounded text-sm font-semibold ${mode === 'qr' ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>QR</button>
                        <button onClick={() => setMode('dni')} className={`flex-1 p-2 rounded text-sm font-semibold ${mode === 'dni' ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>DNI</button>
                    </div>
                </div>

                {error && <div className="mb-4 bg-red-100 text-red-700 p-3 rounded-lg w-full text-center font-bold text-sm shadow-sm">{error}</div>}

                {mode === 'qr' && !scanResult && !isCameraOpen && !loading && (
                    <div className="w-full flex flex-col items-center justify-center py-12">
                        <button 
                            onClick={startScanner}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 px-8 rounded-full shadow-lg transition transform hover:scale-105 flex items-center gap-3 text-lg">
                            Escanear entrada
                        </button>
                        <p className="mt-6 text-gray-500 text-center text-sm px-4">
                            Presione para escanear el codigo QR
                        </p>
                    </div>
                )}

                {mode === 'qr' && isCameraOpen && !scanResult && !loading && (
                    <div className="w-full bg-white p-3 rounded-xl shadow-sm border border-gray-200">
                        <div id="reader" className="w-full rounded-lg overflow-hidden border-2 border-dashed border-gray-300"></div>
                        <button 
                            onClick={stopScanner}
                            className="mt-4 w-full bg-red-100 text-red-700 font-bold py-3 rounded-lg hover:bg-red-200 transition">
                            Cancelar
                        </button>
                    </div>
                )}

                {mode === 'dni' && !scanResult && !loading && (
                    <form onSubmit={validateByDni} className="w-full bg-white p-4 rounded-xl shadow-sm space-y-3">
                        <div>
                            <label className="text-xs font-semibold text-gray-600">DNI del asistente</label>
                            <input type="text" className="w-full p-2 border rounded" value={dniInput} onChange={e => setDniInput(e.target.value.replace(/\D/g, ''))} placeholder="12345678" />
                        </div>
                        <button type="submit" className="w-full bg-emerald-500 text-white font-bold py-2 rounded">Validar por DNI</button>
                    </form>
                )}

                {loading && (
                    <div className="w-full flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-500 mb-4"></div>
                        <p className="text-center text-emerald-600 font-bold animate-pulse text-lg">Validando entrada...</p>
                    </div>
                )}

                {scanResult && !loading && (
                    <div className="bg-white p-6 rounded-xl shadow-lg w-full mt-2 border-t-4 border-emerald-500">
                        {scanResult.valid ? (
                            <>
                                <h3 className="text-2xl font-bold text-center text-emerald-600 mb-4">Entrada Valida</h3>
                                {scanResult.legacy && (
                                    <p className="text-center text-sm text-amber-600 mb-2">Validacion legado - {scanResult.validated_entries} entradas validadas</p>
                                )}
                                <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
                                    {scanResult.entry && (
                                        <>
                                            <p className="flex justify-between"><span className="font-semibold text-gray-500">DNI:</span> <span>{scanResult.entry.attendee_dni}</span></p>
                                            <p className="flex justify-between"><span className="font-semibold text-gray-500">Es gratis:</span> <span>{scanResult.entry.is_free ? 'Si' : 'No'}</span></p>
                                        </>
                                    )}
                                    {scanResult.sale && (
                                        <>
                                            <p className="flex justify-between"><span className="font-semibold text-gray-500">Evento:</span> <span>{scanResult.event?.title}</span></p>
                                            <p className="flex justify-between"><span className="font-semibold text-gray-500">Cant:</span> <span>{scanResult.sale.quantity} (pagas {scanResult.sale.paid_quantity}, gratis {scanResult.sale.free_quantity})</span></p>
                                            {scanResult.sale.competitor_dni && <p className="flex justify-between"><span className="font-semibold text-gray-500">Competidor:</span> <span>{scanResult.sale.competitor_dni}</span></p>}
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <h3 className="text-xl font-bold text-center text-red-600 mb-4">{scanResult.message || 'No valida'}</h3>
                        )}
                        <button onClick={handleRescan} className="w-full mt-4 bg-gray-200 text-gray-800 font-bold py-2 rounded">Volver a escanear</button>
                    </div>
                )}
            </div>
        </div>
    );
}
